// Plain-HTML test harness for the in-house AR try-on engine.
// It imports the REAL engine (no reimplementation) so this page exercises the
// exact code that ships — the point is live-hardware calibration of the knobs in
// renderer.ts that can't be checked headless.
import { TryOnEngine } from '../../src/lib/tryon/engine.ts';
import { PLACEHOLDER_FRAME } from '../../src/lib/tryon/frames.ts';
import { t, STYLE_LABELS } from '../../src/lib/tryon/i18n.ts';

const $ = (id) => document.getElementById(id);
const video = $('video');
const canvas = $('canvas');

const engine = new TryOnEngine({ mode: 'web' });

// Capability probe up front so unsupported devices get an honest message.
const caps = engine.getCapabilities();
$('caps').textContent = caps.supported
  ? `soporte: OK · delegate ${caps.recommendedDelegate}`
  : `no soportado: ${t('error.' + (caps.reason ?? 'unsupported'))}`;
if (!caps.supported) {
  $('startBtn').disabled = true;
  $('gateStatus').textContent = t('error.' + (caps.reason ?? 'unsupported'));
}

const HINT_PILL = { ok: 'ok', 'no-face': 'warn', 'too-far': 'warn', 'too-close': 'warn', 'extreme-angle': 'warn', 'low-light': 'warn', 'multi-face': 'bad' };

function setHint(hint, status) {
  const el = $('hint');
  if (status === 'tracking' && hint !== 'ok') {
    el.textContent = t('hint.' + hint);
    el.classList.add('show');
  } else if (status === 'searching') {
    el.textContent = t('hint.no-face');
    el.classList.add('show');
  } else {
    el.classList.remove('show');
  }
  const pill = $('hintPill');
  pill.textContent = hint;
  pill.className = 'pill ' + (HINT_PILL[hint] ?? '');
}

// Cheap fit estimate: compare measured face width to the frame's real front
// width (2·lens + bridge, mm) — same metafields a real PDP would feed in.
function fitLabel(faceWidthMm) {
  if (!faceWidthMm) return null;
  const frontMm = PLACEHOLDER_FRAME.lensWidthMm * 2 + PLACEHOLDER_FRAME.bridgeMm;
  const ratio = frontMm / faceWidthMm; // ~1 = flush to temples
  if (ratio < 0.82) return { key: 'fit.narrow', cls: 'warn' };
  if (ratio > 0.98) return { key: 'fit.wide', cls: 'warn' };
  return { key: 'fit.fits', cls: 'ok' };
}

function render(state) {
  $('status').textContent = state.status;
  $('fps').textContent = state.fps || '—';
  setHint(state.hint, state.status);

  if (state.status === 'error' && state.error) {
    $('gate').classList.remove('hidden');
    $('controls').classList.add('hidden');
    $('gateStatus').textContent = t(state.error.messageKey);
    $('startBtn').disabled = !state.error.recoverable;
  }

  const m = state.measurement;
  $('pd').textContent = m.pdMm ? m.pdMm.toFixed(1) + ' mm' : t('pd.measuring');
  $('faceW').textContent = m.faceWidthMm ? m.faceWidthMm.toFixed(1) + ' mm' : '—';
  $('conf').textContent = m.samples ? Math.round(m.confidence * 100) + '%' : '—';

  const fit = fitLabel(m.faceWidthMm);
  const fp = $('fitPill');
  fp.textContent = fit ? t(fit.key) : '—';
  fp.className = 'pill ' + (fit ? fit.cls : '');

  const shape = engine.getFaceShape();
  if (shape) {
    $('shape').textContent = t('shape.' + shape.shape);
    $('recommends').textContent = shape.recommends.map((s) => STYLE_LABELS[s] ?? s).join(', ');
  }
}

engine.on((ev) => {
  if (ev.type === 'state') render(ev.state);
  if (ev.type === 'ready') {
    $('gate').classList.add('hidden');
    $('controls').classList.remove('hidden');
  }
  if (ev.type === 'capture') {
    const img = $('shot');
    img.src = ev.dataUrl;
    img.classList.add('show');
  }
});

// Colour swatches → swap variant GLB live (exercises renderer.setFrame).
const sw = $('swatches');
for (const v of PLACEHOLDER_FRAME.variants ?? []) {
  const b = document.createElement('button');
  b.className = 'swatch';
  b.style.background = v.swatchColor;
  b.title = v.label;
  b.setAttribute('aria-pressed', v.id === (PLACEHOLDER_FRAME.variants[0]?.id) ? 'true' : 'false');
  b.onclick = () => {
    sw.querySelectorAll('.swatch').forEach((s) => s.setAttribute('aria-pressed', 'false'));
    b.setAttribute('aria-pressed', 'true');
    engine.setFrame(PLACEHOLDER_FRAME, v.glbUrl);
  };
  sw.appendChild(b);
}

$('startBtn').addEventListener('click', async () => {
  $('startBtn').disabled = true;
  $('gateStatus').textContent = t('loading.sdk');
  // Errors surface through the state listener (render()), which re-shows the gate.
  await engine.start({ canvas, video, frame: PLACEHOLDER_FRAME });
});

$('captureBtn').addEventListener('click', () => engine.capture());
$('closeBtn').addEventListener('click', () => {
  engine.stop();
  $('controls').classList.add('hidden');
  $('gate').classList.remove('hidden');
  $('startBtn').disabled = false;
  $('gateStatus').textContent = '';
});
